/**
 * Striker Run — self-contained endless-runner + shooter game runtime hosted in a react-native-webview
 * (native) / srcDoc iframe (web). Ported from the playground prototype (tiktok-minigame/striker-run/
 * index.html) with the host bridge added; the app copy here is authoritative (specs/striker-run/spec.md).
 *
 * Why a string module: the whole game (canvas loop + input + WebAudio sfx) ships with the app as one HTML
 * document with zero RN-bundle imports and zero network dependencies — it always works offline.
 *
 * Bridge protocol (window.ReactNativeWebView.postMessage, window.parent fallback; JSON):
 *   web → RN:  { type:'ready' } | { type:'run-end', goals, coins, distance, best, cause }
 *              | { type:'load-error' }
 *   RN → web:  none (host ref supports reload only)
 *
 * All gameplay feel-numbers live in the runtime's TUNE block; escalation is capped at TUNE.ESCALATION_CAP
 * and each run carries 1-3 collectible coins in total (cosmetic — nothing banks server-side in v1).
 *
 * Copy guard: no "bet" (pick/prediction wording), no real team/player/tournament names, no "unlimited".
 */

export function buildStrikerRunGameHtml(): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover" />
<title>Striker Run — prototype</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; -webkit-tap-highlight-color:transparent; }
  html,body { width:100%; height:100%; overflow:hidden; background:#0B1220; touch-action:none;
    font-family:-apple-system,system-ui,sans-serif; user-select:none; -webkit-user-select:none; }
  #stage { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; }
  canvas { display:block; max-width:100%; max-height:100%; }
</style>
</head>
<body>
<div id="stage"><canvas id="c" width="720" height="1080"></canvas></div>
<script>
window.__errs = [];
window.onerror = function(m, s, l, c, e){ window.__errs.push(String(m) + ' @' + l + ':' + c); };
// Bridge: native WebView first, srcDoc-iframe parent as the web fallback (same contract as Wall Breaker).
window.__post = function(m){
  var s = JSON.stringify(m);
  try { if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) { window.ReactNativeWebView.postMessage(s); return; } } catch (e) {}
  try { if (window.parent && window.parent !== window) window.parent.postMessage(s, '*'); } catch (e) {}
};
</script>
<script>
(function(){
  'use strict';
  try {
  // ================= TUNABLES =================
  // Every gameplay feel-number lives here. All escalation clamps at ESCALATION_CAP goals.
  var TUNE = {
    ESCALATION_CAP: 20,        // goals; past this nothing gets harder
    // world / running
    SCROLL_BASE: 0.34,         // px/ms world scroll at 0 goals
    SCROLL_PER_GOAL: 0.023,    // px/ms added per goal (0.80 at cap)
    PX_PER_M: 38,              // scroll px per meter on the distance HUD
    // lanes / dodge
    LANES: [180, 360, 540],    // lane center xs
    PLAYER_Y: 830,             // striker's fixed screen y
    SWIPE_PX: 42,              // horizontal drag px that counts as a lane swipe
    LANE_SNAP_MS: 60,          // lane-change lerp time constant (smaller = snappier)
    // tackles
    TACKLE_EVERY_BASE: 1500,   // ms between tackle spawns at 0 goals
    TACKLE_EVERY_PER_GOAL: 45, // ms shaved per goal
    TACKLE_EVERY_MIN: 650,     // floor
    TELEGRAPH_MS: 620,         // crouch + warning pulse before the slide
    SLIDE_MS: 380,             // how long the slide-across takes
    DOUBLE_FROM: 8,            // goals needed before 2-lane tackles can spawn
    DOUBLE_CHANCE: 0.3,        // chance a spawn is a 2-lane tackle (one lane always safe)
    // coins — scarce rewards on the safe path; each run has 1-3 in TOTAL
    COINS_RUN_MIN: 1,          // min coins available per run
    COINS_RUN_MAX: 3,          // max coins available per run
    COIN_CHANCE: 0.25,         // chance a tackle row carries one (while the run's budget lasts)
    COIN_R: 16,                // coin radius (also drives the pickup hitbox)
    // shoot phase
    SHOOT_EVERY_MS: 7000,      // running time between goal chances
    SHOOT_WINDOW_MS: 1500,     // flick-or-die window once the goal is set
    GOAL_SLIDE_MS: 380,        // goal frame slide-in time
    FLIGHT_MS: 260,            // ball flight time
    MIN_DRAG: 10,              // px; shorter release = tap-to-shoot at the tap point
    AIM_SCALE_X: 3.6,          // drag px -> aim px (higher = twitchier crosshair)
    AIM_SCALE_Y: 2.8,
    KEY_SHOT_INSET: 48,        // px inside each post for ◀/▶ keyboard shots
    KEY_SHOT_HIGH: 46,         // px below the bar for keyboard shots (top corners)
    // keeper
    KEEPER_PERIOD_BASE: 2000,  // ms per patrol cycle at 0 goals
    KEEPER_PERIOD_PER_GOAL: 60,
    KEEPER_PERIOD_MIN: 800,
    KEEPER_REACH_BASE: 92,     // px glove reach at 0 goals
    KEEPER_REACH_PER_GOAL: 2.6,
    KEEPER_JUMP_MAX: 70,       // px vertical leap (fixed; top corners stay open)
    // pacing
    GOAL_PAUSE_MS: 700,        // celebration pause before running resumes
    MILESTONES: { 5:'ON FIRE 🔥', 10:'DOUBLE DIGITS ⚡', 15:'UNSTOPPABLE 🏆', 20:'FINAL FORM 👑' },
  };

  // ---------- World ----------
  var W = 720, H = 1080;
  var GOAL_L = 110, GOAL_R = 610, BAR_Y = 140, LINE_Y = 340;
  var GOAL_CX = (GOAL_L + GOAL_R) / 2;
  var GLOVE_BASE_Y = LINE_Y - 88;
  var BALL_R = 22;

  function capped(g){ return Math.min(g, TUNE.ESCALATION_CAP); }
  function scrollSpeed(g){ return TUNE.SCROLL_BASE + TUNE.SCROLL_PER_GOAL * capped(g); }
  function tackleEvery(g){ return Math.max(TUNE.TACKLE_EVERY_MIN, TUNE.TACKLE_EVERY_BASE - TUNE.TACKLE_EVERY_PER_GOAL * capped(g)); }
  function keeperPeriod(g){ return Math.max(TUNE.KEEPER_PERIOD_MIN, TUNE.KEEPER_PERIOD_BASE - TUNE.KEEPER_PERIOD_PER_GOAL * capped(g)); }
  function keeperReach(g){ return TUNE.KEEPER_REACH_BASE + TUNE.KEEPER_REACH_PER_GOAL * capped(g); }

  var TACKLE_LINES = ['Cleaned out! 🧹', 'He ate the grass.', 'Saw you coming a mile off.', 'Legs? Gone.'];
  var SAVE_LINES = ['Read it all day 🧤', 'Gloves like magnets.', 'Not today.', 'Is that all you got?'];
  var OFF_LINES = ['Row Z. 🚀', 'The frame matters.', 'Corner flag says hi.'];
  var SLOW_LINES = ['Window shut. Shoot faster!', 'Frozen at the big moment 🥶', 'The keeper yawned.'];

  // ---------- State ----------
  var canvas = document.getElementById('c'), ctx = canvas.getContext('2d');
  var state = 'running';   // running | shootwindow | dragging | flight | goalpause | over
  var score = 0, best = Number(localStorage.getItem('sr-best') || 0);
  var distance = 0, scrollY = 0;
  var player = { lane: 1, x: TUNE.LANES[1] };
  var tackles = [];            // { lanes:[i], y, phase:'telegraph'|'slide', t, side, x }
  var coins = [], coinCount = 0;   // coins: { x, y }
  var coinBudget = TUNE.COINS_RUN_MIN + ((Math.random() * (TUNE.COINS_RUN_MAX - TUNE.COINS_RUN_MIN + 1)) | 0);
  var sparkles = [];               // pickup pops: { x, y, t0 }
  var tackleTimer = 0, shootTimer = 0;
  var goalIn = 0, shootReady = false, shootClockLeft = 0;
  var keeper = { x: GOAL_CX, phase: 0, diveX: null, diveFromX: 0, diveY: 0, diveT: 0 };
  var ball = { x: 0, y: 0, s: 1 };
  var aim = { x: GOAL_CX, y: 230 };
  var drag = null, swipe = null, flight = null, flash = null, shake = 0, shakeT = 0;
  var lastTs = 0, nextRunAt = 0, trail = [], ripple = null;
  var overLine = '', overSub = '';

  // ---------- Audio ----------
  var actx = null;
  function ac(){ if(!actx){ try{ actx = new (window.AudioContext||window.webkitAudioContext)(); }catch(e){} }
    if (actx && actx.state==='suspended') actx.resume(); return actx; }
  function tone(type, f0, f1, t, vol){
    var c2 = ac(); if(!c2) return;
    var o = c2.createOscillator(), g = c2.createGain();
    o.type = type; o.frequency.setValueAtTime(f0, c2.currentTime);
    o.frequency.exponentialRampToValueAtTime(Math.max(1,f1), c2.currentTime + t);
    g.gain.setValueAtTime(vol, c2.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, c2.currentTime + t);
    o.connect(g); g.connect(c2.destination); o.start(); o.stop(c2.currentTime + t + 0.02);
  }
  function sfxWhistle(){ tone('square', 2300, 2280, 0.09, 0.1); setTimeout(function(){ tone('square', 2300, 2250, 0.2, 0.1); }, 150); }
  function sfxKick(){ tone('sine', 150, 60, 0.13, 0.5); }
  function sfxSwish(){ tone('sine', 700, 1200, 0.07, 0.08); }
  function sfxThud(){ tone('square', 180, 70, 0.2, 0.3); }
  function sfxSave(){ tone('sawtooth', 260, 90, 0.4, 0.13); }
  function sfxTick(){ tone('square', 900, 900, 0.03, 0.05); }
  function sfxRoar(){
    tone('triangle', 523, 523, 0.4, 0.15);
    setTimeout(function(){ tone('triangle', 659, 659, 0.4, 0.15); }, 70);
    setTimeout(function(){ tone('triangle', 784, 784, 0.5, 0.15); }, 140);
    tone('sawtooth', 80, 55, 0.55, 0.12);
  }
  function sfxMilestone(){ tone('triangle', 392, 784, 0.35, 0.16); setTimeout(function(){ tone('triangle', 784, 1046, 0.3, 0.14); }, 120); }
  function sfxCoin(){ tone('sine', 1320, 1980, 0.08, 0.12); setTimeout(function(){ tone('sine', 1760, 2200, 0.07, 0.09); }, 60); }

  // ---------- Flow ----------
  function spawnTackle(forcedLane){
    var lanes;
    if (forcedLane !== undefined) {
      lanes = [forcedLane];
    } else if (score >= TUNE.DOUBLE_FROM && Math.random() < TUNE.DOUBLE_CHANCE) {
      var safe = (Math.random() * 3) | 0;              // one lane always stays open
      lanes = [0, 1, 2].filter(function(l){ return l !== safe; });
    } else {
      lanes = [(Math.random() * 3) | 0];
    }
    for (var i = 0; i < lanes.length; i++) {
      var lane = lanes[i];
      var side = lane === 0 ? 1 : (lane === 2 ? -1 : (Math.random() < 0.5 ? -1 : 1));
      tackles.push({ lane: lane, y: -80, phase: 'telegraph', t: 0, side: side,
                     x: TUNE.LANES[lane] + side * 150 });
    }
    // A coin on the safe path: each run has 1-3 coins in TOTAL (coinBudget), doled out
    // one at a time on random tackle rows, in a lane the row leaves open.
    if (coinBudget > 0 && Math.random() < TUNE.COIN_CHANCE) {
      var open = [0, 1, 2].filter(function(l){ return lanes.indexOf(l) === -1; });
      if (open.length) {
        var coinLane = open[(Math.random() * open.length) | 0];
        coins.push({ x: TUNE.LANES[coinLane], y: -80 });
        coinBudget--;
      }
    }
  }
  function startShoot(){
    tackles.length = 0; coins.length = 0;              // clear the pitch for the strike
    goalIn = 0; shootReady = false;
    shootClockLeft = TUNE.SHOOT_WINDOW_MS;
    keeper.diveX = null; keeper.diveY = 0; keeper.diveT = 0; keeper.phase = Math.random() * Math.PI * 2;
    drag = null; flight = null; trail.length = 0;
    state = 'shootwindow';
  }
  function resumeRun(){
    state = 'running';
    tackleTimer = 0; shootTimer = 0;
    flight = null; trail.length = 0;
  }
  function endRun(title, lines, cause){
    state = 'over'; overLine = title;
    overSub = lines[(Math.random() * lines.length) | 0];
    if (score > best) { best = score; localStorage.setItem('sr-best', String(best)); }
    window.__post({ type: 'run-end', goals: score, coins: coinCount, distance: distance | 0, best: best, cause: cause });
  }
  function restart(){
    score = 0; distance = 0; scrollY = 0;
    player.lane = 1; player.x = TUNE.LANES[1];
    tackles.length = 0; coins.length = 0; coinCount = 0; sparkles.length = 0;
    coinBudget = TUNE.COINS_RUN_MIN + ((Math.random() * (TUNE.COINS_RUN_MAX - TUNE.COINS_RUN_MIN + 1)) | 0);
    tackleTimer = 0; shootTimer = 0;
    goalIn = 0; flight = null; drag = null; swipe = null; trail.length = 0;
    state = 'running';
    sfxWhistle();
  }

  function keeperXAt(extraMs){
    var p = keeperPeriod(score);
    var ph = keeper.phase + (extraMs / p) * Math.PI * 2;
    var amp = (GOAL_R - GOAL_L)/2 - 40;
    return GOAL_CX + Math.sin(ph) * amp;
  }

  function shoot(){
    sfxKick();
    var toX = aim.x, toY = aim.y;
    var inFrame = toX > GOAL_L + 10 && toX < GOAL_R - 10 && toY > BAR_Y + 10 && toY < LINE_Y - 4;
    var kx = keeperXAt(TUNE.FLIGHT_MS);
    keeper.diveFromX = keeper.x;
    keeper.diveX = Math.max(GOAL_L+24, Math.min(GOAL_R-24, kx));
    var gloveY = Math.max(toY, GLOVE_BASE_Y - TUNE.KEEPER_JUMP_MAX);
    keeper.diveY = Math.max(0, GLOVE_BASE_Y - gloveY);
    keeper.diveT = 0;
    var dist = Math.hypot(toX - keeper.diveX, toY - gloveY);
    var saved = inFrame && dist <= keeperReach(score);
    var nearMiss = inFrame && !saved && dist <= keeperReach(score) + 34;
    flight = { t0: performance.now(), fromX: player.x, fromY: TUNE.PLAYER_Y - 10, toX: toX, toY: toY,
               outcome: !inFrame ? 'off' : (saved ? 'save' : 'goal'), nearMiss: nearMiss };
    state = 'flight';
  }

  function land(){
    var f = flight;
    if (f.outcome === 'goal') {
      score++;
      shake = 14; shakeT = performance.now();
      ripple = { x: f.toX, y: f.toY, t0: performance.now() };
      sfxRoar();
      var ms = TUNE.MILESTONES[score];
      if (ms) sfxMilestone();
      flash = { t0: performance.now(), color: ms ? '#FCD34D' : '#C8FF4D',
                label: ms || ('+1  ·  ' + score), ms: ms ? 620 : 360, big: !!ms };
      state = 'goalpause';
      nextRunAt = performance.now() + TUNE.GOAL_PAUSE_MS + (ms ? 400 : 0);
    } else if (f.outcome === 'save') {
      sfxSave();
      shake = 10; shakeT = performance.now();
      flash = { t0: performance.now(), color: '#FF5C4D', label: f.nearMiss ? 'FINGERTIPS!' : 'SAVED', ms: 800 };
      endRun(f.nearMiss ? 'SO CLOSE' : 'SAVED', SAVE_LINES, 'saved');
    } else {
      sfxSave();
      flash = { t0: performance.now(), color: '#FF5C4D', label: 'OFF TARGET', ms: 800 };
      endRun('OFF TARGET', OFF_LINES, 'off');
    }
  }

  // ---------- Input ----------
  function evPos(e){
    var t = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]) || e;
    var r = canvas.getBoundingClientRect();
    return { x: (t.clientX - r.left) * (W / r.width), y: (t.clientY - r.top) * (H / r.height) };
  }
  function onDown(e){
    e.preventDefault(); ac();
    if (state === 'over') { restart(); return; }
    var p = evPos(e);
    if (state === 'running') { swipe = { sx: p.x, sy: p.y, used: false }; return; }
    if (state === 'shootwindow') {
      drag = { sx: p.x, sy: p.y };
      aim.x = GOAL_CX; aim.y = 230;
      state = 'dragging';
    }
  }
  function onMove(e){
    e.preventDefault();
    var p = evPos(e);
    if (state === 'running' && swipe && !swipe.used) {
      var dx = p.x - swipe.sx;
      if (Math.abs(dx) >= TUNE.SWIPE_PX) {
        var dir = dx > 0 ? 1 : -1;
        var next = Math.max(0, Math.min(2, player.lane + dir));
        if (next !== player.lane) { player.lane = next; sfxSwish(); }
        swipe.used = true;
      }
      return;
    }
    if (state === 'dragging' && drag) {
      aim.x = Math.max(GOAL_L - 60, Math.min(GOAL_R + 60, GOAL_CX + (p.x - drag.sx) * TUNE.AIM_SCALE_X));
      aim.y = Math.max(BAR_Y - 26, Math.min(LINE_Y - 8, 235 + (p.y - drag.sy) * TUNE.AIM_SCALE_Y));
    }
  }
  function onUp(e){
    e.preventDefault();
    if (state === 'running') { swipe = null; return; }
    if (state !== 'dragging' || !drag) return;
    var p = evPos(e);
    var moved = Math.hypot(p.x - drag.sx, p.y - drag.sy);
    drag = null;
    if (moved < TUNE.MIN_DRAG) {
      // Tap-to-shoot: fire at the tap point (clamped to aim bounds) so a click always shoots.
      aim.x = Math.max(GOAL_L - 60, Math.min(GOAL_R + 60, p.x));
      aim.y = Math.max(BAR_Y - 26, Math.min(LINE_Y - 8, p.y));
    }
    shoot();
  }
  ['touchstart','mousedown'].forEach(function(n){ canvas.addEventListener(n, onDown, { passive:false }); });
  ['touchmove','mousemove'].forEach(function(n){ canvas.addEventListener(n, onMove, { passive:false }); });
  ['touchend','mouseup','touchcancel'].forEach(function(n){ canvas.addEventListener(n, onUp, { passive:false }); });

  // Keyboard: ◀▶ (or A/D) dodge lanes; in the SHOOT window ◀▲▶ fire at top-left / high-center / top-right.
  function shootAt(x, y){ aim.x = x; aim.y = y; shoot(); }
  function onKey(e){
    var k = e.key;
    var handled = ['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','a','d','w','s',' ','Enter'];
    if (handled.indexOf(k) === -1) return;
    e.preventDefault(); ac();
    if (state === 'over') { restart(); return; }
    var left = k === 'ArrowLeft' || k === 'a', right = k === 'ArrowRight' || k === 'd';
    var up = k === 'ArrowUp' || k === 'w', down = k === 'ArrowDown' || k === 's';
    if (state === 'running') {
      var dir = left ? -1 : (right ? 1 : 0);
      if (!dir) return;
      var next = Math.max(0, Math.min(2, player.lane + dir));
      if (next !== player.lane) { player.lane = next; sfxSwish(); }
      return;
    }
    if (state === 'shootwindow') {
      if (left) shootAt(GOAL_L + TUNE.KEY_SHOT_INSET, BAR_Y + TUNE.KEY_SHOT_HIGH);
      else if (right) shootAt(GOAL_R - TUNE.KEY_SHOT_INSET, BAR_Y + TUNE.KEY_SHOT_HIGH);
      else if (up) shootAt(GOAL_CX, BAR_Y + TUNE.KEY_SHOT_HIGH);
      else if (down) shootAt(GOAL_CX, LINE_Y - 30);
    }
  }
  window.addEventListener('keydown', onKey);

  // ---------- Update ----------
  function update(ts){
    var dt = lastTs ? Math.min(50, ts - lastTs) : 16;
    lastTs = ts;

    // Lane lerp — snappy exponential approach with a visible lean while moving.
    var targetX = TUNE.LANES[player.lane];
    player.x += (targetX - player.x) * (1 - Math.exp(-dt / TUNE.LANE_SNAP_MS));

    // Goal frame slides in for the shoot phase, back out while running.
    var wantGoal = (state === 'shootwindow' || state === 'dragging' || state === 'flight' || state === 'goalpause');
    if (wantGoal) {
      goalIn = Math.min(1, goalIn + dt / TUNE.GOAL_SLIDE_MS);
      if (goalIn >= 1 && !shootReady && state === 'shootwindow') { shootReady = true; sfxTick(); }
    } else {
      goalIn = Math.max(0, goalIn - dt / 300);
    }

    if (state === 'running') {
      var sp = scrollSpeed(score);
      scrollY += sp * dt;
      distance += (sp * dt) / TUNE.PX_PER_M;

      tackleTimer += dt;
      if (tackleTimer >= tackleEvery(score)) { tackleTimer = 0; spawnTackle(); }
      shootTimer += dt;
      if (shootTimer >= TUNE.SHOOT_EVERY_MS) startShoot();

      for (var i = tackles.length - 1; i >= 0; i--) {
        var tk = tackles[i];
        tk.y += sp * dt;
        tk.t += dt;
        if (tk.phase === 'telegraph' && tk.t >= TUNE.TELEGRAPH_MS) { tk.phase = 'slide'; tk.t = 0; }
        if (tk.phase === 'slide') {
          var k = Math.min(1, tk.t / TUNE.SLIDE_MS);
          var e2 = 1 - (1-k)*(1-k);
          tk.x = TUNE.LANES[tk.lane] + tk.side * 150 - tk.side * 190 * e2;  // slides across, ends past center
          // Hitbox only while he's actually sliding/down — telegraph is safe.
          if (Math.abs(tk.y - TUNE.PLAYER_Y) < 60 && Math.abs(tk.x - player.x) < 62) {
            sfxThud();
            shake = 12; shakeT = performance.now();
            flash = { t0: performance.now(), color: '#FF5C4D', label: 'TACKLED!', ms: 800 };
            endRun('TACKLED', TACKLE_LINES, 'tackled');
            break;
          }
        }
        if (tk.y > H + 120) tackles.splice(i, 1);
      }

      for (var ci = coins.length - 1; ci >= 0; ci--) {
        var cn = coins[ci];
        cn.y += sp * dt;
        if (Math.abs(cn.y - TUNE.PLAYER_Y) < TUNE.COIN_R + 30 && Math.abs(cn.x - player.x) < TUNE.COIN_R + 34) {
          coinCount++;
          sfxCoin();
          sparkles.push({ x: cn.x, y: cn.y, t0: performance.now() });
          coins.splice(ci, 1);
        } else if (cn.y > H + 60) {
          coins.splice(ci, 1);
        }
      }
    }
    for (var si = sparkles.length - 1; si >= 0; si--) {
      if (ts - sparkles[si].t0 > 340) sparkles.splice(si, 1);
    }

    // Keeper patrol / dive (only meaningful while the goal is on screen).
    if (goalIn > 0) {
      if (keeper.diveX === null) {
        keeper.phase += (dt / keeperPeriod(score)) * Math.PI * 2;
        var amp = (GOAL_R - GOAL_L)/2 - 40;
        keeper.x = GOAL_CX + Math.sin(keeper.phase) * amp;
      } else {
        keeper.diveT = Math.min(1, keeper.diveT + dt / 180);
        keeper.x += (keeper.diveX - keeper.x) * keeper.diveT;
      }
    }

    // SHOOT window countdown — expiring without a shot ends the run.
    if ((state === 'shootwindow' || state === 'dragging') && shootReady) {
      var prev = shootClockLeft;
      shootClockLeft -= dt;
      if (shootClockLeft <= 700 && Math.floor(prev/200) !== Math.floor(shootClockLeft/200)) sfxTick();
      if (shootClockLeft <= 0) {
        flash = { t0: performance.now(), color: '#FF5C4D', label: 'TOO SLOW!', ms: 800 };
        sfxSave();
        endRun('TOO SLOW', SLOW_LINES, 'slow');
      }
    }

    if (state === 'flight' && flight) {
      var fk = Math.min(1, (ts - flight.t0) / TUNE.FLIGHT_MS);
      var fe = 1 - (1-fk)*(1-fk);
      ball.x = flight.fromX + (flight.toX - flight.fromX) * fe;
      ball.y = flight.fromY + (flight.toY - flight.fromY) * fe;
      ball.s = 1 - 0.45 * fe;
      trail.push({ x: ball.x, y: ball.y, t: ts });
      if (fk >= 1) land();
    }
    while (trail.length && ts - trail[0].t > 260) trail.shift();

    if (state === 'goalpause' && ts >= nextRunAt) resumeRun();
  }

  // ---------- Render ----------
  function draw(ts){
    ctx.save();
    if (shake > 0) {
      var sk = Math.max(0, 1 - (ts - shakeT)/160) * shake;
      if (sk <= 0.3) shake = 0;
      ctx.translate((Math.random()-0.5)*sk, (Math.random()-0.5)*sk);
    }
    ctx.clearRect(-20, -20, W+40, H+40);
    drawPitch();
    if (goalIn > 0) drawGoal(ts);
    drawCoins(ts);
    drawTackles(ts);
    if (state !== 'over') drawPlayer(ts);
    if (state === 'dragging') drawAim();
    drawBallFlight(ts);
    drawHud(ts);
    drawFlash(ts);
    if (state === 'over') drawOver();
    ctx.restore();
  }

  function drawPitch(){
    var sky = ctx.createLinearGradient(0, 0, 0, H);
    sky.addColorStop(0, '#0B1220'); sky.addColorStop(0.16, '#141A38');
    sky.addColorStop(0.3, '#173B24'); sky.addColorStop(1, '#10301A');
    ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);
    // Mow stripes scrolling down.
    ctx.globalAlpha = 0.07; ctx.fillStyle = '#FFF';
    for (var y = -(scrollY % 240); y < H; y += 240) ctx.fillRect(0, y, W, 120);
    ctx.globalAlpha = 1;
    // Touchlines.
    ctx.strokeStyle = 'rgba(244,251,246,.22)'; ctx.lineWidth = 6;
    ctx.beginPath(); ctx.moveTo(52, 0); ctx.lineTo(52, H); ctx.moveTo(W-52, 0); ctx.lineTo(W-52, H); ctx.stroke();
    // Lane divider dashes, scrolling with the world.
    ctx.strokeStyle = 'rgba(244,251,246,.1)'; ctx.lineWidth = 4;
    var off = scrollY % 80;
    [270, 450].forEach(function(x){
      ctx.beginPath();
      for (var y2 = -80 + off; y2 < H; y2 += 80) { ctx.moveTo(x, y2); ctx.lineTo(x, y2 + 38); }
      ctx.stroke();
    });
    // Purple glow under the striker's lane — brand accent + focus cue.
    var glow = ctx.createRadialGradient(player.x, TUNE.PLAYER_Y + 10, 10, player.x, TUNE.PLAYER_Y + 10, 130);
    glow.addColorStop(0, 'rgba(139,92,246,.28)'); glow.addColorStop(1, 'rgba(139,92,246,0)');
    ctx.fillStyle = glow; ctx.fillRect(player.x - 130, TUNE.PLAYER_Y - 120, 260, 260);
  }

  function drawGoal(ts){
    ctx.save();
    ctx.translate(0, -(1 - goalIn) * 400);
    // Net with ripple.
    ctx.strokeStyle = 'rgba(244,251,246,.18)'; ctx.lineWidth = 1;
    var rip = null;
    if (ripple) { var rk = (ts - ripple.t0)/380; if (rk >= 1) ripple = null; else rip = { x: ripple.x, y: ripple.y, k: rk }; }
    for (var x = GOAL_L; x <= GOAL_R; x += 24) {
      ctx.beginPath();
      for (var y = BAR_Y; y <= LINE_Y; y += 8) {
        var ox = 0;
        if (rip) { var d3 = Math.hypot(x-rip.x, y-rip.y); ox = Math.sin(d3/18 - rip.k*14) * 6 * Math.max(0,1-rip.k) * Math.max(0, 1 - d3/220); }
        if (y === BAR_Y) ctx.moveTo(x+ox, y); else ctx.lineTo(x+ox, y);
      }
      ctx.stroke();
    }
    for (var y2 = BAR_Y; y2 <= LINE_Y; y2 += 24) { ctx.beginPath(); ctx.moveTo(GOAL_L, y2); ctx.lineTo(GOAL_R, y2); ctx.stroke(); }
    ctx.strokeStyle = '#F4FBF6'; ctx.lineWidth = 10; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(GOAL_L, LINE_Y); ctx.lineTo(GOAL_L, BAR_Y); ctx.lineTo(GOAL_R, BAR_Y); ctx.lineTo(GOAL_R, LINE_Y); ctx.stroke();
    drawKeeper();
    ctx.restore();
  }

  function drawKeeper(){
    ctx.save();
    var leap = keeper.diveX !== null ? keeper.diveY * Math.sin(Math.min(1, keeper.diveT) * Math.PI * 0.5) : 0;
    ctx.translate(keeper.x, LINE_Y - leap);
    if (keeper.diveX !== null) {
      var dx = keeper.diveX - keeper.diveFromX;
      var flatness = Math.min(1, Math.abs(dx) / 170);
      var vertical = Math.min(1, keeper.diveY / 100);
      var maxLean = (0.45 + 1.0 * flatness) * (1 - 0.55 * vertical);
      var lean = (dx >= 0 ? 1 : -1) * maxLean * Math.sin(Math.min(1, keeper.diveT) * Math.PI * 0.5);
      ctx.rotate(lean);
    }
    var spread = keeper.diveX !== null ? 44 : 26 + Math.sin(performance.now()/300)*4;
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(-13, -34, 10, 34); ctx.fillRect(3, -34, 10, 34);
    var kit = ctx.createLinearGradient(0, -92, 0, -30);
    kit.addColorStop(0, '#FBBF24'); kit.addColorStop(1, '#D97706');
    ctx.fillStyle = kit;
    ctx.fillRect(-17, -92, 34, 62);
    ctx.fillRect(-spread-9, -84, 12, 42);
    ctx.fillRect(spread-3, -84, 12, 42);
    ctx.fillStyle = '#E5E7EB';
    ctx.beginPath(); ctx.arc(-spread-3, -88, 9, 0, Math.PI*2); ctx.fill();
    ctx.beginPath(); ctx.arc(spread+3, -88, 9, 0, Math.PI*2); ctx.fill();
    ctx.fillStyle = '#F3C89D';
    ctx.beginPath(); ctx.arc(0, -104, 14, 0, Math.PI*2); ctx.fill();
    ctx.restore();
  }

  function drawPlayer(ts){
    ctx.save();
    ctx.translate(player.x, TUNE.PLAYER_Y);
    var lean = (TUNE.LANES[player.lane] - player.x) * 0.0022;
    ctx.rotate(lean);
    var run = Math.sin(ts / 90) * 9;
    // Shadow + dribbled ball at his feet (running only).
    ctx.fillStyle = 'rgba(0,0,0,.35)';
    ctx.beginPath(); ctx.ellipse(0, 8, 34, 9, 0, 0, Math.PI*2); ctx.fill();
    if (state === 'running' || state === 'over') {
      var bob = Math.sin(ts / 110) * 8;
      ctx.fillStyle = '#F4FBF6';
      ctx.beginPath(); ctx.arc(bob, -4, 12, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle = '#1F2937';
      ctx.beginPath(); ctx.arc(bob, -4, 4, 0, Math.PI*2); ctx.fill();
    }
    // Legs.
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(-11 + run*0.4, -32, 9, 32);
    ctx.fillRect(3 - run*0.4, -32, 9, 32);
    // Purple kit torso + arms.
    var kit = ctx.createLinearGradient(0, -80, 0, -28);
    kit.addColorStop(0, '#8B5CF6'); kit.addColorStop(1, '#6D28D9');
    ctx.fillStyle = kit;
    ctx.fillRect(-15, -80, 30, 50);
    ctx.fillRect(-24, -74, 8, 34 + run*0.3);
    ctx.fillRect(16, -74, 8, 34 - run*0.3);
    // Number on the back.
    ctx.fillStyle = 'rgba(255,255,255,.85)'; ctx.font = '800 20px -apple-system,system-ui,sans-serif';
    ctx.textAlign = 'center'; ctx.fillText('9', 0, -46);
    // Head.
    ctx.fillStyle = '#F3C89D';
    ctx.beginPath(); ctx.arc(0, -92, 13, 0, Math.PI*2); ctx.fill();
    ctx.restore();
  }

  function drawTackles(ts){
    for (var i = 0; i < tackles.length; i++) {
      var tk = tackles[i];
      var laneX = TUNE.LANES[tk.lane];
      if (tk.phase === 'telegraph') {
        // Warning pulse in the lane — this is the dodge cue.
        var pulse = 0.5 + 0.5 * Math.sin(ts / 90);
        ctx.strokeStyle = 'rgba(255,92,77,' + (0.45 + 0.45*pulse) + ')';
        ctx.lineWidth = 5;
        ctx.beginPath(); ctx.arc(laneX, tk.y, 30 + pulse*14, 0, Math.PI*2); ctx.stroke();
        ctx.fillStyle = 'rgba(255,92,77,' + (0.6 + 0.4*pulse) + ')';
        ctx.font = '800 34px -apple-system,system-ui,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('!', laneX, tk.y + 12);
        // Crouched defender at the lane edge, winding up.
        ctx.save();
        ctx.translate(tk.x, tk.y);
        ctx.rotate(tk.side * -0.2);
        ctx.fillStyle = '#0F172A'; ctx.fillRect(-9, -20, 7, 20); ctx.fillRect(2, -20, 7, 20);
        ctx.fillStyle = '#DC2626'; ctx.fillRect(-12, -52, 24, 34);
        ctx.fillStyle = '#F3C89D'; ctx.beginPath(); ctx.arc(0, -62, 10, 0, Math.PI*2); ctx.fill();
        ctx.restore();
      } else {
        // Sliding: low, legs out, dust behind.
        ctx.save();
        ctx.translate(tk.x, tk.y);
        ctx.rotate(tk.side * -1.15);
        ctx.fillStyle = '#0F172A'; ctx.fillRect(-8, -46, 7, 30); ctx.fillRect(3, -50, 7, 34);
        ctx.fillStyle = '#DC2626'; ctx.fillRect(-13, -18, 26, 30);
        ctx.fillStyle = '#F3C89D'; ctx.beginPath(); ctx.arc(0, 20, 10, 0, Math.PI*2); ctx.fill();
        ctx.restore();
        ctx.globalAlpha = 0.35;
        ctx.fillStyle = '#D6D3D1';
        for (var d = 1; d <= 3; d++) {
          ctx.beginPath();
          ctx.arc(tk.x + tk.side * (30 + d*22), tk.y + 6, 10 - d*2, 0, Math.PI*2); ctx.fill();
        }
        ctx.globalAlpha = 1;
      }
    }
  }

  function drawCoins(ts){
    for (var i = 0; i < coins.length; i++) {
      var cn = coins[i];
      // Spin: squash x by a phase offset per coin so runs shimmer.
      var sq = Math.abs(Math.sin(ts / 260 + cn.y * 0.02));
      ctx.save();
      ctx.translate(cn.x, cn.y);
      ctx.scale(0.35 + 0.65 * sq, 1);
      var g = ctx.createRadialGradient(-4, -5, 2, 0, 0, TUNE.COIN_R);
      g.addColorStop(0, '#FDE68A'); g.addColorStop(0.7, '#FBBF24'); g.addColorStop(1, '#B45309');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(0, 0, TUNE.COIN_R, 0, Math.PI*2); ctx.fill();
      ctx.strokeStyle = 'rgba(180,83,9,.8)'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(0, 0, TUNE.COIN_R - 4, 0, Math.PI*2); ctx.stroke();
      ctx.restore();
    }
    for (var s2 = 0; s2 < sparkles.length; s2++) {
      var sk = sparkles[s2], k = (ts - sk.t0) / 340;
      if (k >= 1) continue;
      ctx.globalAlpha = 1 - k;
      ctx.strokeStyle = '#FDE68A'; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.arc(sk.x, sk.y, TUNE.COIN_R + k * 30, 0, Math.PI*2); ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }

  function drawAim(){
    var inF = aim.x > GOAL_L && aim.x < GOAL_R && aim.y > BAR_Y;
    ctx.strokeStyle = inF ? '#C8FF4D' : '#FF5C4D'; ctx.lineWidth = 4;
    ctx.beginPath(); ctx.arc(aim.x, aim.y, 22, 0, Math.PI*2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(aim.x-32, aim.y); ctx.lineTo(aim.x+32, aim.y);
    ctx.moveTo(aim.x, aim.y-32); ctx.lineTo(aim.x, aim.y+32); ctx.stroke();
  }

  function drawBallFlight(ts){
    if (state !== 'flight' && state !== 'goalpause') return;
    for (var t2 = 0; t2 < trail.length; t2++) {
      var tr = trail[t2], a = (ts - tr.t)/260;
      ctx.globalAlpha = Math.max(0, 0.35*(1-a));
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath(); ctx.arc(tr.x, tr.y, BALL_R*0.55*(1-a*0.5), 0, Math.PI*2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    var bg = ctx.createRadialGradient(ball.x-6*ball.s, ball.y-7*ball.s, 2, ball.x, ball.y, BALL_R*ball.s);
    bg.addColorStop(0, '#FFF'); bg.addColorStop(0.75, '#DDE1E6'); bg.addColorStop(1, '#9CA3AF');
    ctx.fillStyle = bg; ctx.beginPath(); ctx.arc(ball.x, ball.y, BALL_R*ball.s, 0, Math.PI*2); ctx.fill();
    ctx.fillStyle = '#1F2937'; ctx.beginPath(); ctx.arc(ball.x, ball.y, 6*ball.s, 0, Math.PI*2); ctx.fill();
  }

  function drawHud(ts){
    ctx.textAlign = 'center';
    var pop = flash && (ts - flash.t0) < 220 ? 1 + 0.35*(1-(ts-flash.t0)/220) : 1;
    ctx.save();
    ctx.translate(W/2, 84); ctx.scale(pop, pop);
    ctx.fillStyle = '#F4FBF6'; ctx.font = '800 68px -apple-system,system-ui,sans-serif';
    ctx.fillText(String(score), 0, 0);
    ctx.restore();
    ctx.fillStyle = 'rgba(244,251,246,.55)'; ctx.font = '600 22px -apple-system,system-ui,sans-serif';
    ctx.fillText('BEST ' + Math.max(best, score) + '   ·   ' + (distance | 0) + ' m', W/2, 118);

    // Coin counter, top-right.
    var cg = ctx.createRadialGradient(W-108, 78, 2, W-104, 82, 13);
    cg.addColorStop(0, '#FDE68A'); cg.addColorStop(0.7, '#FBBF24'); cg.addColorStop(1, '#B45309');
    ctx.fillStyle = cg;
    ctx.beginPath(); ctx.arc(W-104, 82, 13, 0, Math.PI*2); ctx.fill();
    ctx.textAlign = 'left';
    ctx.fillStyle = '#FDE68A'; ctx.font = '800 28px -apple-system,system-ui,sans-serif';
    ctx.fillText(String(coinCount), W-84, 92);
    ctx.textAlign = 'center';

    if ((state === 'shootwindow' || state === 'dragging') && shootReady) {
      var frac = Math.max(0, shootClockLeft / TUNE.SHOOT_WINDOW_MS);
      var bw = 300;
      ctx.fillStyle = 'rgba(255,255,255,.14)';
      ctx.fillRect(W/2 - bw/2, 396, bw, 10);
      ctx.fillStyle = frac > 0.4 ? '#C8FF4D' : '#FF5C4D';
      ctx.fillRect(W/2 - bw/2, 396, bw*frac, 10);
      var sp2 = 1 + 0.1 * Math.sin(ts/110);
      ctx.save();
      ctx.translate(W/2, 452); ctx.scale(sp2, sp2);
      ctx.fillStyle = '#C8FF4D'; ctx.font = '800 44px -apple-system,system-ui,sans-serif';
      ctx.fillText('SHOOT!', 0, 0);
      ctx.restore();
    }

    if (state === 'running' && score === 0 && distance < 40) {
      ctx.fillStyle = 'rgba(244,251,246,.62)'; ctx.font = '600 24px -apple-system,system-ui,sans-serif';
      ctx.fillText('Swipe or ◀ ▶ to dodge — flick, tap, or ◀ ▲ ▶ to score', W/2, H-60);
    }
  }

  function drawFlash(ts){
    if (!flash) return;
    var k = (ts - flash.t0)/flash.ms;
    if (k >= 1) { if (state !== 'over') flash = null; return; }
    var a = k < 0.25 ? k/0.25 : 1-(k-0.25)/0.75;
    ctx.globalAlpha = a * (flash.big ? 0.38 : 0.22);
    ctx.fillStyle = flash.color;
    ctx.fillRect(0, 0, W, H);
    ctx.globalAlpha = Math.min(1, a*1.5);
    ctx.fillStyle = flash.color;
    ctx.font = (flash.big ? '800 78px' : '800 56px') + ' -apple-system,system-ui,sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(flash.label, W/2, H/2 - 60);
    ctx.globalAlpha = 1;
  }

  function drawOver(){
    ctx.fillStyle = 'rgba(11,18,32,.72)';
    ctx.fillRect(0, H/2 - 10, W, 280);
    ctx.textAlign = 'center';
    ctx.fillStyle = '#F4FBF6'; ctx.font = '800 54px -apple-system,system-ui,sans-serif';
    ctx.fillText(overLine, W/2, H/2 + 66);
    ctx.fillStyle = '#FCD34D'; ctx.font = '600 26px -apple-system,system-ui,sans-serif';
    ctx.fillText(overSub, W/2, H/2 + 110);
    ctx.fillStyle = 'rgba(244,251,246,.7)'; ctx.font = '700 28px -apple-system,system-ui,sans-serif';
    ctx.fillText(score + ' goals  ·  ' + coinCount + ' coins  ·  ' + (distance | 0) + ' m  ·  best ' + best, W/2, H/2 + 168);
    ctx.fillStyle = 'rgba(200,255,77,.85)'; ctx.font = '700 26px -apple-system,system-ui,sans-serif';
    ctx.fillText('tap to run it back', W/2, H/2 + 214);
  }

  // DEMO ONLY: debug hook for headless verification and hazard-level inspection.
  window.__srDebug = function(cmd, v){
    if (cmd === 'snap') return {
      state: state, score: score, lane: player.lane, playerX: Math.round(player.x),
      tackles: tackles.map(function(t){ return { lane: t.lane, y: Math.round(t.y), phase: t.phase }; }),
      goalIn: Math.round(goalIn * 100) / 100, clock: Math.round(shootClockLeft),
      keeperX: Math.round(keeper.x), dist: distance | 0, errs: window.__errs.length,
      coins: coinCount, coinBudget: coinBudget,
      coinsOnPitch: coins.map(function(c){ return { x: c.x, y: Math.round(c.y) }; }),
    };
    if (cmd === 'score') { score = v | 0; return score; }
    if (cmd === 'shoot') { if (state === 'running') startShoot(); return state; }
    if (cmd === 'tackle') { spawnTackle(v); return tackles.length; }
    if (cmd === 'restart') { restart(); return state; }
  };

  function frame(ts){ update(ts); draw(ts); requestAnimationFrame(frame); }
  requestAnimationFrame(frame);
  window.__post({ type: 'ready' });
  // Hidden tabs never fire rAF — drive the loop with a timer so embedded/preview hosts still animate.
  setInterval(function(){ if (document.hidden) { var t = performance.now(); update(t); draw(t); } }, 33);
  } catch (e) {
    window.__errs.push('boot: ' + String(e));
    window.__post({ type: 'load-error' });
  }
})();
</script>
</body>
</html>
`;
}
